import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Dialog, DialogActions, DialogContent, DialogTitle, PopoverBasicHelp, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { ParseHelper, DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { Permission, RetentionUnit, type Repository, type SpaceDefaultLifecycleReleaseRetentionPolicyResource, type SpaceDefaultLifecycleTentacleRetentionPolicyResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { useState } from "react";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import ComponentRow from "~/components/ComponentRow";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { RedirectAs404 } from "~/components/NotFound/NotFound";
import { PageContent } from "~/components/PageContent/PageContent";
import { FormSection, Text, minimum } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
interface ReleaseRetentionPoliciesPageLoaderData {
    defaultReleasePolicy: SpaceDefaultLifecycleReleaseRetentionPolicyResource;
    defaultTentaclePolicy: SpaceDefaultLifecycleTentacleRetentionPolicyResource;
}
interface ReleaseRetentionPoliciesPageProps {
    spaceId: string;
    loaderData: ReleaseRetentionPoliciesPageLoaderData;
}
export async function releaseRetentionPoliciesPageLoader(repository: Repository): Promise<ReleaseRetentionPoliciesPageLoaderData> {
    if (!isFeatureToggleEnabled("centralized-retention-lifecycle")) {
        return {
            defaultReleasePolicy: {
                RetentionType: "LifecycleRelease",
                SpaceId: "",
                Id: "",
                Name: "",
                Strategy: "Forever",
            },
            defaultTentaclePolicy: {
                RetentionType: "LifecycleTentacle",
                SpaceId: "",
                Id: "",
                Name: "",
                Strategy: "Forever",
            },
        };
    }
    const defaultReleasePolicyRequest = repository.SpaceDefaultRetentionPolicies.getDefaultByType("LifecycleRelease");
    const defaultTentaclePolicyRequest = repository.SpaceDefaultRetentionPolicies.getDefaultByType("LifecycleTentacle");
    return {
        // Repository will always return a SpaceDefaultLifecycleReleaseRetentionPolicyResource, and SpaceDefaultLifecycleTentacleRetentionPolicyResource, so we can safely cast it
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        defaultReleasePolicy: (await defaultReleasePolicyRequest) as SpaceDefaultLifecycleReleaseRetentionPolicyResource,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        defaultTentaclePolicy: (await defaultTentaclePolicyRequest) as SpaceDefaultLifecycleTentacleRetentionPolicyResource,
    };
}
export default function ReleaseRetentionPoliciesPage(props: ReleaseRetentionPoliciesPageProps) {
    if (!isFeatureToggleEnabled("centralized-retention-lifecycle")) {
        return <RedirectAs404 />;
    }
    return <ReleaseRetentionPoliciesPageInternal {...props}/>;
}
function ReleaseRetentionPoliciesPageInternal(props: ReleaseRetentionPoliciesPageProps) {
    const { loaderData } = props;
    const defaultReleaseRetentionPageTitle = "Release Retention";
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const { model, setModel, createSaveAction, formProps, isDisabled } = useForm({
        formName: "Edit Default Release Retention Policy",
        submitPermission: { permission: [Permission.RetentionAdminister] },
        initialModel: { defaultReleasePolicy: loaderData.defaultReleasePolicy, defaultTentaclePolicy: loaderData.defaultTentaclePolicy },
        onSubmit: async (repository, model) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const newReleasePolicy = repository.SpaceDefaultRetentionPolicies.updateDefault(model.defaultReleasePolicy) as Promise<SpaceDefaultLifecycleReleaseRetentionPolicyResource>;
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const newTentaclePolicy = repository.SpaceDefaultRetentionPolicies.updateDefault(model.defaultTentaclePolicy) as Promise<SpaceDefaultLifecycleTentacleRetentionPolicyResource>;
            return { defaultReleasePolicy: await newReleasePolicy, defaultTentaclePolicy: await newTentaclePolicy };
        },
        updateFormModelAfterSubmit: (model) => model,
        afterSubmit: async () => setSaveDialogOpen(false),
    });
    return (<PageContent header={{
            title: defaultReleaseRetentionPageTitle,
            primaryAction: {
                type: "button",
                label: isDisabled.value && isDisabled.reason === "no permissions" ? isDisabled.missingPermissionsLabel : "Save",
                disabled: isDisabled.value,
                onClick: () => setSaveDialogOpen(true),
            },
            showBreadcrumbBackIcon: true,
            breadcrumbs: [{ label: "Retention Policies", pageUrl: links.retentionPoliciesPage.generateUrl({ spaceId: props.spaceId }) }],
        }}>
            <Dialog open={saveDialogOpen} dialogWidth="480px">
                <DialogTitle>Update retention policy?</DialogTitle>
                <DialogContent>This policy will apply to all lifecycles and/or packages on tentacles using the “Default retention policy”</DialogContent>
                <DialogActions>
                    <ActionButton label="Cancel" onClick={() => setSaveDialogOpen(false)} type={ActionButtonType.Secondary}/>
                    <ActionButton {...createSaveAction({ saveButtonLabel: "Update", saveButtonBusyLabel: "Updating..." })} type={ActionButtonType.Primary}/>
                </DialogActions>
            </Dialog>
            <BorderedContent>
                <Form {...formProps}>
                    <FormSection title={<span className={styles.formSectionTitleContainer}>
                                Default Retention Policy
                                <PopoverBasicHelp description="This retention policy will be applied to all releases that use the “Default retention policy”" placement="right-start"/>
                            </span>} help="This retention policy dictates how long releases and deployments are kept for.">
                        <RadioButtonGroup title="How long should we keep releases and deployments?" value={model.defaultReleasePolicy.Strategy} onChange={(newStrategy) => setModel((prevModel) => ({
            ...prevModel,
            defaultReleasePolicy: {
                ...prevModel.defaultReleasePolicy,
                Strategy: newStrategy,
                ...(newStrategy === "Count"
                    ? {
                        // Default values for Count strategy
                        QuantityToKeep: prevModel.defaultReleasePolicy.QuantityToKeep ?? 3,
                        Unit: prevModel.defaultReleasePolicy.Unit ?? "Items",
                    }
                    : {}),
            },
        }))} accessibleName="Release retention policy">
                            <RadioButton label="Keep all" value="Forever"/>
                            <RadioButton label="Keep a limited number" value="Count"/>
                            {model.defaultReleasePolicy.Strategy === "Count" && (<ComponentRow>
                                    <span>
                                        <Text type="number" min={1} value={model.defaultReleasePolicy.QuantityToKeep?.toString() ?? ""} onChange={(newQuanitity) => setModel((prevModel) => ({
                ...prevModel,
                defaultReleasePolicy: {
                    ...prevModel.defaultReleasePolicy,
                    QuantityToKeep: ParseHelper.safeParseInt(newQuanitity, 0),
                },
            }))} validate={minimum("Please provide a value greater than zero.", 1)} accessibleName="number of release units"/>
                                    </span>
                                    <span>
                                        <DeprecatedSelect value={model.defaultReleasePolicy.Unit ?? ""} onChange={(newUnit) => setModel((prevModel) => ({
                ...prevModel,
                defaultReleasePolicy: {
                    ...prevModel.defaultReleasePolicy,
                    Unit: parseRetentionPolicyUnit(newUnit),
                },
            }))} items={[
                { value: RetentionUnit.Days, text: "Days" },
                { value: RetentionUnit.Items, text: "Releases" },
            ]}/>
                                    </span>
                                </ComponentRow>)}
                        </RadioButtonGroup>
                        <RadioButtonGroup title="How long should we keep extracted packages and files on disk on Tentacles?" value={model.defaultTentaclePolicy.Strategy} onChange={(newStrategy) => setModel((prevModel) => ({
            ...prevModel,
            defaultTentaclePolicy: {
                ...prevModel.defaultTentaclePolicy,
                Strategy: newStrategy,
                ...(newStrategy === "Count"
                    ? {
                        // Default values for Count strategy
                        QuantityToKeep: prevModel.defaultTentaclePolicy.QuantityToKeep ?? 3,
                        Unit: prevModel.defaultTentaclePolicy.Unit ?? "Items",
                    }
                    : {}),
            },
        }))} accessibleName="Tentacle retention policy">
                            <RadioButton label="Keep all" value="Forever"/>
                            <RadioButton label="Keep a limited number" value="Count"/>
                            {model.defaultTentaclePolicy.Strategy === "Count" && (<ComponentRow>
                                    <span>
                                        <Text type="number" min={1} value={model.defaultTentaclePolicy.QuantityToKeep?.toString() ?? ""} onChange={(newQuantityToKeep) => setModel((prevModel) => ({
                ...prevModel,
                defaultTentaclePolicy: {
                    ...prevModel.defaultTentaclePolicy,
                    QuantityToKeep: ParseHelper.safeParseInt(newQuantityToKeep, 0),
                },
            }))} validate={minimum("Please provide a value greater than zero.", 1)} accessibleName="number of tentacle units"/>
                                    </span>
                                    <span>
                                        <DeprecatedSelect value={model.defaultTentaclePolicy.Unit ?? ""} onChange={(newUnit) => setModel((prevModel) => ({
                ...prevModel,
                defaultTentaclePolicy: {
                    ...prevModel.defaultTentaclePolicy,
                    Unit: parseRetentionPolicyUnit(newUnit),
                },
            }))} items={[
                { value: RetentionUnit.Days, text: "Days" },
                { value: RetentionUnit.Items, text: "Releases" },
            ]}/>
                                    </span>
                                </ComponentRow>)}
                        </RadioButtonGroup>
                    </FormSection>
                </Form>
            </BorderedContent>
        </PageContent>);
}
function parseRetentionPolicyUnit(unit: string | undefined): RetentionUnit {
    switch (unit) {
        case "Days":
            return RetentionUnit.Days;
        case "Items":
            return RetentionUnit.Items;
        default:
            return RetentionUnit.Days; // Default to Days if an unknown unit is provided
    }
}
const styles = {
    formSectionTitleContainer: css({
        display: "flex",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignItems: "center",
        gap: space[4],
    }),
};
